import React from 'react';
import { SkeletonBlock, SkeletonRegion } from './BrandPresenceSkeleton';
import { BP_CARD_CLASSES } from './brandPresenceClasses';

/** Rows a first load draws: one page of the list. */
const FIRST_LOAD_ROW_COUNT = 8;

/** Name widths that vary row to row, so the skeleton reads as a list of pages rather than a grid. */
const NAME_WIDTHS = ['w-56', 'w-44', 'w-64', 'w-40', 'w-52', 'w-60', 'w-48', 'w-36'];

/**
 * The cells of one loading row, shaped like the row that will replace it: a checkbox, a page name
 * over its address, the health figure with its meter, a stack of check badges, tag chips and the age.
 */
function InventorySkeletonCells({ index, columnCount }: { index: number; columnCount: number }) {
  // The fixed columns: checkbox, name, then whatever the reader has switched on, then age and action.
  const middleColumns = Math.max(0, columnCount - 4);

  return (
    <>
      <td className="px-4 py-4 w-12">
        <SkeletonBlock className="h-4 w-4 rounded" />
      </td>
      <td className="px-4 py-4">
        <SkeletonBlock className={`h-4 ${NAME_WIDTHS[index % NAME_WIDTHS.length]}`} />
        <SkeletonBlock className="mt-2 h-3 w-40" />
      </td>
      {Array.from({ length: middleColumns }, (_unused, column) => (
        <td key={column} className="px-4 py-4">
          {column % 3 === 0 ? (
            <div className="space-y-1.5">
              <SkeletonBlock className="h-4 w-10" />
              <SkeletonBlock className="h-1.5 w-16 rounded-full" />
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              <SkeletonBlock className="h-5 w-20 rounded-full" />
              {(index + column) % 2 === 0 ? <SkeletonBlock className="h-5 w-14 rounded-full" /> : null}
            </div>
          )}
        </td>
      ))}
      <td className="px-4 py-4">
        <SkeletonBlock className="h-4 w-14" />
      </td>
      <td className="px-4 py-4">
        <SkeletonBlock className="ml-auto h-8 w-16 rounded-lg" />
      </td>
    </>
  );
}

/** Loading rows for a list that is already on screen: another page, a new sort, a refresh. */
export function ContentInventorySkeletonRows({ rowCount, columnCount }: { rowCount: number; columnCount: number }) {
  return (
    <>
      {Array.from({ length: rowCount }, (_unused, index) => (
        <tr key={`skeleton-${index}`} aria-hidden="true">
          <InventorySkeletonCells index={index} columnCount={columnCount} />
        </tr>
      ))}
    </>
  );
}

/**
 * The whole inventory while its first answer is on the way: the header, the three figures, the
 * toolbar and a page of rows, each where the real one will land.
 */
export function ContentInventorySkeleton({ columnCount }: { columnCount: number }) {
  return (
    <SkeletonRegion label="Loading your pages" className="w-full h-full bg-slate-50">
      <div className="max-w-[1800px] mx-auto px-6 py-8">
        <div className="mb-8">
          <SkeletonBlock className="h-3 w-44" />
          <SkeletonBlock className="mt-3 h-8 w-72 rounded-lg" />
          <SkeletonBlock className="mt-3 h-4 w-[28rem] max-w-full" />
        </div>
        <div className="mb-6 flex flex-wrap gap-4">
          {[0, 1, 2].map((card) => (
            <div key={card} className={`grow basis-[200px] min-w-[200px] p-5 ${BP_CARD_CLASSES}`}>
              <SkeletonBlock className="h-3 w-36" />
              <SkeletonBlock className="mt-4 h-9 w-24 rounded-lg" />
              <SkeletonBlock className="mt-4 h-1.5 w-full rounded-full" />
            </div>
          ))}
        </div>
        {/* Shaped like the list it stands for: the tools wrap on a narrow screen, and the table
            scrolls sideways in its own box. */}
        <div className={BP_CARD_CLASSES}>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200/70 px-4 py-3">
            <div className="flex flex-1 flex-wrap items-center gap-3">
              <SkeletonBlock className="h-9 w-72 max-w-full rounded-lg" />
              <SkeletonBlock className="h-8 w-32 rounded-full" />
            </div>
            <div className="flex items-center gap-2">
              <SkeletonBlock className="h-9 w-24 rounded-lg" />
              <SkeletonBlock className="h-9 w-32 rounded-lg" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <tbody className="divide-y divide-slate-100">
                <ContentInventorySkeletonRows rowCount={FIRST_LOAD_ROW_COUNT} columnCount={columnCount} />
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </SkeletonRegion>
  );
}
